import { DEFAULT_PAGE_SIZE, type Paginated } from '@cf/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';

import { api, ApiError, refreshSession, session } from './api';

/** GET a list/resource; the query key is the path so mutations can invalidate by prefix. */
export function useApi<T>(path: string | null, options: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: [path],
    // An empty answer (e.g. no company saved yet) is `null`: a query can't resolve to undefined.
    queryFn: async () => ((await api<T>(path!)) ?? null) as T,
    enabled: path != null,
    ...options,
  });
}

/**
 * A server-paginated list. The page number lives in the URL (`?page=3`, key `urlKey`) so Back and
 * shared links keep it; `urlKey: null` keeps it local (lists inside modals / drawers).
 * Screens call `setPage(1)` when a filter changes. The previous page stays on screen while the
 * next one loads.
 */
export function usePaged<T>(
  path: string | null,
  {
    pageSize = DEFAULT_PAGE_SIZE,
    urlKey = 'page',
  }: { pageSize?: number; urlKey?: string | null } = {},
) {
  const [params, setParams] = useSearchParams();
  const [localPage, setLocalPage] = useState(1);
  const page = urlKey ? Math.max(1, Number(params.get(urlKey)) || 1) : localPage;
  const setPage = useCallback(
    (p: number) => {
      if (!urlKey) return setLocalPage(p);
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) next.delete(urlKey);
        else next.set(urlKey, String(p));
        return next;
      });
    },
    [urlKey, setParams],
  );
  const full = path
    ? `${path}${path.includes('?') ? '&' : '?'}page=${page}&pageSize=${pageSize}`
    : null;
  const query = useQuery({
    queryKey: [full],
    queryFn: () => api<Paginated<T>>(full!),
    enabled: full != null,
    placeholderData: keepPreviousData,
  });
  const pager = query.data
    ? { page, pageSize, total: query.data.total, onPage: setPage }
    : undefined;
  return { ...query, page, pageSize, setPage, pager };
}

/** POST (create) or PUT (update, when `id` is given) on a collection path. */
/**
 * After a write: the collection's lists refresh now; every other screen is marked stale and
 * reloads when shown again (a client's address, a product… appear on invoices, orders, stock).
 */
function staleAfterWrite(qc: ReturnType<typeof useQueryClient>, collection: string) {
  void qc.invalidateQueries({ refetchType: 'none' });
  return qc.invalidateQueries({
    predicate: (q) => String(q.queryKey[0]).startsWith(collection),
  });
}

export function useSave<TBody, TResult = unknown>(collection: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: TBody }) =>
      api<TResult>(id ? `${collection}/${id}` : collection, {
        method: id ? 'PUT' : 'POST',
        json: body,
      }),
    onSuccess: () => staleAfterWrite(qc, collection),
  });
}

export function useRemove(collection: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`${collection}/${id}`, { method: 'DELETE' }),
    onSuccess: () => staleAfterWrite(qc, collection),
  });
}

/** Opens an authenticated PDF (labels, documents) in a new tab. */
export async function openPdf(path: string): Promise<void> {
  // Open synchronously (popup blockers), then fill once the blob is ready.
  const win = window.open('', '_blank');
  let res = await fetch(`/api${path}`, { headers: authHeader(), credentials: 'include' });
  if (res.status === 401 && (await refreshSession())) {
    res = await fetch(`/api${path}`, { headers: authHeader(), credentials: 'include' });
  }
  if (!res.ok) {
    win?.close();
    throw new ApiError(res.status, 'PDF_ERROR', `HTTP ${res.status}`);
  }
  const url = URL.createObjectURL(await res.blob());
  if (win) win.location.href = url;
  else window.location.href = url;
}

/** Downloads an authenticated file (Excel exports) under the server-provided name. */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let res = await fetch(`/api${path}`, { headers: authHeader(), credentials: 'include' });
  if (res.status === 401 && (await refreshSession())) {
    res = await fetch(`/api${path}`, { headers: authHeader(), credentials: 'include' });
  }
  if (!res.ok) throw new ApiError(res.status, 'DOWNLOAD_ERROR', `HTTP ${res.status}`);
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1];
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), {
    href: url,
    download: name ?? fallbackName,
  });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

const authHeader = (): Record<string, string> => {
  const t = session.token();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/** Maps an API validation error (zod issues) to { field: message }. */
export function fieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError) || err.code !== 'VALIDATION_ERROR') return {};
  const issues = (
    err.details as { issues?: { path: (string | number)[]; message: string }[] } | undefined
  )?.issues;
  const out: Record<string, string> = {};
  for (const i of issues ?? []) {
    const key = String(i.path[0] ?? '_');
    out[key] ??= i.message;
  }
  return out;
}
